import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CURRENCIES, type Currency } from '@wishare/shared';
import { Loading } from '../components/ui.js';
import type { PrefilledCard } from '../components/ItemForm.js';

/**
 * Приём готовой карточки от закладки-кнопки.
 *
 * Закладка исполняется на странице магазина, собирает название, цену и фото
 * прямо из документа и открывает этот адрес с параметрами. Здесь мы только
 * раскладываем их по местам и уводим человека к форме добавления.
 *
 * Почему не разбираем ссылку сами, как в приёме «поделиться»: закладку заводят
 * ровно для тех магазинов, куда наш сервер не попадает. Повторный разбор вернул
 * бы пустоту и затёр бы то, что человек уже принёс со страницы.
 *
 * Данные идут в адресной строке, а не запросом на сервер. Это осознанно: пока
 * человек не нажал «Добавить», ничего никуда не отправляется, и сервер о его
 * покупках не знает.
 */

const ХРАНИЛИЩЕ = 'wishare:prefilled-card';

export function AddFromPagePage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const card = разобратьПараметры(params);

    // Через sessionStorage, а не только состоянием перехода: если человек ещё
    // не вошёл, его уведёт на страницу входа, и состояние потеряется вместе
    // с карточкой, которую он уже собрал.
    if (card) {
      try {
        sessionStorage.setItem(ХРАНИЛИЩЕ, JSON.stringify(card));
      } catch {
        /* приватный режим без хранилища — обойдёмся состоянием перехода */
      }
    }

    navigate('/', { replace: true, state: card ? { prefilledCard: card } : undefined });
  }, [params, navigate]);

  return <Loading label="Забираем карточку со страницы…" />;
}

/** Достаёт карточку из параметров адреса. Без ссылки карточки нет. */
export function разобратьПараметры(params: URLSearchParams): PrefilledCard | null {
  const url = params.get('url')?.trim();
  if (!url || !/^https?:\/\//i.test(url)) return null;

  const цена = Number.parseInt(params.get('price') ?? '', 10);
  const валюта = params.get('currency')?.trim().toUpperCase();

  return {
    url,
    title: обрезать(params.get('title'), 200),
    description: обрезать(params.get('description'), 500),
    // Цена приходит уже в копейках. Отрицательную и нечисловую отбрасываем:
    // пустое поле человек заполнит сам, а неверная цена собьёт с толку.
    priceMinor: Number.isFinite(цена) && цена > 0 ? цена : null,
    /*
     * Валюту сверяем со списком приложения, а не с шаблоном из трёх букв.
     * Иначе «ДЕНЬГИ» отсеются, а вполне настоящая гривна пройдёт — и человек
     * получит отказ при сохранении, не поняв, за что.
     */
    currency: валюта && (CURRENCIES as readonly string[]).includes(валюта) ? (валюта as Currency) : undefined,
    /*
     * Картинку принимаем только по http или https.
     *
     * Ссылку на этот адрес может составить кто угодно и прислать её человеку —
     * закладка тут не единственный источник. Сервер такую картинку всё равно
     * отклонит при сохранении, но человек узнает об этом только в момент,
     * когда нажмёт «Добавить», и не поймёт, за что. Отсекаем на входе.
     */
    imageUrl: картинка(params.get('image')),
    /* Страница не похожа на карточку товара — форма скажет об этом человеку. */
    uncertain: params.get('uncertain') === '1',
  };
}

/** Забирает карточку, оставленную закладкой, и сразу подчищает за собой. */
export function взятьКарточкуИзХранилища(): PrefilledCard | null {
  try {
    const сырое = sessionStorage.getItem(ХРАНИЛИЩЕ);
    if (!сырое) return null;
    sessionStorage.removeItem(ХРАНИЛИЩЕ);
    const карточка = JSON.parse(сырое) as PrefilledCard;
    return карточка?.url ? карточка : null;
  } catch {
    return null;
  }
}

function обрезать(значение: string | null, предел: number): string | undefined {
  const текст = значение?.trim();
  return текст ? текст.slice(0, предел) : undefined;
}

/** Адрес картинки: только http и https, всё остальное молча отбрасываем. */
function картинка(значение: string | null): string | undefined {
  const текст = обрезать(значение, 2000);
  if (!текст) return undefined;
  try {
    const адрес = new URL(текст);
    return адрес.protocol === 'http:' || адрес.protocol === 'https:' ? текст : undefined;
  } catch {
    return undefined;
  }
}
